// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import api from '../../../../common/api';
import config from '../../../../share/config';
import handler from '../../../../share/util/apiResultValidator';
import customTable from '../../../../share/components/vue/PaginationTable';
import resource from '../../../../share/util/resourceMap';
import util from '../../../../share/util/utils';
import loading from '../../../../share/components/vue/ProcessingIcon';
import diagnoseResource from '../../../../share/util/diagnose';

export default {
  name: "vehicleControlHistoryPage",
  props: {
    // デバッグ環境のみの動作確認
    testModeVisible: { type: Boolean, required: false }
  },
  data() {
    return {
      processing: false,
      recollectedData: [],
      measuredDataColumns: [],
      measuredDataItems: [],
      protocol: ''
    };
  },

  watch: {
    language() {
      this.updateColumns();
    },
  },

  components: {
    'custom-table': customTable,
    'processing-view': loading,
  },

  methods: {
    ...mapActions('auth', ['logout']),

    /**
     * 制御作動履歴一覧取得
     * @param {String} projectId プロジェクトID
     * @param {String} measuresId 計測データID
     */
    async createVehicleControlHistoryMeasuresList(projectId, measuresId) {
      try {
        this.processing = true;
        const params = { region_info: this.cachedProjectDataSource };
        const vehicleControlHistoryEndPoint = '/vehicleControlHistories/' + projectId + '/' + measuresId;
        const resVehicleControlHistory = await api.getParamCall(config.VCH, vehicleControlHistoryEndPoint, params, this.operationScreen);

        handler.validate(handler.validateTypes.all, resVehicleControlHistory, this, null, () => {
          this.recollectedData = resVehicleControlHistory.data.project;
          // 先頭から取得する。BMCがない場合もプロトコルは取得される。
          this.protocol = this.recollectedData.data[0]?.protocol_type;
          this.updateColumns();
          let bmcList = [];
          switch (resVehicleControlHistory.data.project.res_type) {
            // 正常
            case config.VCH_RESPONSE_TYPE_OK:
              // 取得データでBMCテーブルを設定
              bmcList = bmcList.concat(resource.createBmcTable(resVehicleControlHistory.data.project.data, this));
              break;
            // 異常、取得なし
            case config.VCH_RESPONSE_TYPE_NG:
            case config.VCH_RESPONSE_TYPE_NO:
              // 空の BMC を表すテーブルアイテムを作成（通信不能）
              // 制御作動履歴が0件の場合ステータスをコードなし、通信不能とする
              bmcList = bmcList.concat(resource.createEmptyBmcTableItem(config.BMC_STATUS_CANNOT_CONNECT));
              break;
            default:
          }
          this.measuredDataItems = bmcList;
          this.measuredDataItems.map(e => {
            // 一覧にフォーカス当てた際に矢印にする
            e.bgStyle = 'dtc-cursor-default';
          });
        }, (result) => {
          if (result.behaviorType === handler.behaviorTypes.logout) this.logout(this);
        });
      } finally {
        this.processing = false;
      }
    },

    /** 初期表示で必要な情報を設定 */
    async updateColumns() {
      const igOnCntVar = util.mkHeaderValue(this.recollectedData.current_igon_time);
      const igOnCnt = this.$t('message.header_vehicle_dtc_table_igon_counts');
      const igOnCounterValue = this.$t('message.header_vehicle_dtc_table_measurement_value') + ': ' + igOnCntVar;
      const igOnTimeVar = util.mkHeaderValue(this.recollectedData.current_igon_mill_sec);
      const igOnTime = this.$t('message.header_vehicle_dtc_table_igon_times');
      const igOnTimeValue = this.$t('message.header_vehicle_dtc_table_measurement_value') + ': ' + igOnTimeVar;
      this.measuredDataColumns = [
        // Phase5のみステータス列を表示する（空で表示される）
        ...(this.protocol === config.PROTOCOL_PHASE5 ? [{ name: 'status', label: 'message.header_vehicle_dtc_table_status', useResource: true }] : []),
        { name: 'bmc', label: 'message.header_vehicle_dtc_table_code' },
        { name: 'description', label: 'message.header_vehicle_dtc_table_description', useResource: true, originalSortFunc: diagnoseResource.sortFuncDescription },
        { name: 'igonCount', label: util.setColums(igOnCnt, igOnCounterValue, "{0} {1}", true), useHtmlBind: true, originalSortFunc: diagnoseResource.sortIgon },
        { name: 'igonTime', label: util.setColums(igOnTime, igOnTimeValue, "{0} {1}", true), useHtmlBind: true, originalSortFunc: diagnoseResource.sortIgon },
        { name: 'counterType', label: 'message.header_vehicle_dtc_table_type' },
        { name: 'bmd', label: 'individual.header_vehicle_bmc_table_bmd', useButton: true, useResource: true, originalSortFunc: diagnoseResource.sortFuncFfd },
      ];
    },

    /**
     * メモボタン押下.
     * DataRecollection.jsのopenMemoModalを呼び出している
     */
    async parentOpenMemoModal() {
      this.$emit('openMemoModal');
    },

    /**
     * ファイルエクスポートボタン押下.
     * DataRecollection.jsのcsvExportを呼び出している
     * csvExportに一覧のカラム、Itemを引数として渡している
     */
    csvExport() {
      this.$emit('csvExport', this.measuredDataColumns, this.measuredDataItems, this.recollectedData);
    },

    /**
     * BMDボタン押下.
     * DataRecollection.jsのBMDModalを呼び出している
     * @param {Array} row 一覧の選択した行
     */
    async rowButtonClickEvent(row) {
      await this.openBMDModal(row);
    },

    /**
     * 親のopenBMDModalを実行する
     * @param {Object} arg 一覧の選択した行
     */
    openBMDModal(arg) {
      this.$emit('openBMDModal', arg);
    },

    /** DataRecollection.jsの関数を呼び出す */
    async debugExport() {
      await this.bmcTestDataExport();
    },

    /** DIDテストデータエクスポート  */
    bmcTestDataExport() {
      this.$emit('bmcTestDataExport', this.recollectedData);
    }
  },

  computed: {
    ...mapState({
      language: state => state.systems.language,
      cachedProjectDataSource: state => state.cache.projectDataSource,
    }),
    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断のうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    }
  },
};